import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDownRegular, DocumentRegular, SearchRegular, CheckmarkRegular } from '@fluentui/react-icons';
import { useLanguage } from '../contexts/LanguageContext';
import { useTextSize } from '../contexts/TextSizeContext';
import messageTemplateService from '../services/messageTemplateService';

/**
 * TemplateSelector Component
 * Displays and allows selection of email templates
 */
export default function TemplateSelector({ isOpen, onToggle }) {
  const { t } = useLanguage();
  const { currentTextSize } = useTextSize();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  // Fetch templates when section is opened
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!isOpen) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch only email templates
        const result = await messageTemplateService.getAll({ channel: 'email' });
        
        if (result.success && result.data) {
          setTemplates(result.data);
        } else {
          setError(result.error || t('compose.loadingTemplates'));
        }
      } catch (err) {
        console.error('Failed to fetch templates:', err);
        setError(err.message || t('compose.loadingTemplates'));
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [isOpen, t]);

  // Filter templates based on search query
  const filteredTemplates = useMemo(() => {
    if (!templates || !Array.isArray(templates)) return [];

    return templates
      .filter(template =>
        String(template.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(template.subject || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(template.body || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
      .sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), 'de', { sensitivity: 'base' })
      );
  }, [templates, searchQuery]);

  // Handle template selection
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    const template = templates.find(t => t.id === templateId);
    
    if (template) {
      console.log('Selected template:', template);
      
      // Insert template content at cursor position using Office.js
      try {
        if (typeof Office !== 'undefined' && Office.context && Office.context.mailbox && Office.context.mailbox.item) {
          const item = Office.context.mailbox.item;
          
          // Set subject if available and subject is empty
          if (template.subject && item.subject) {
            // Only set subject if it's empty
            item.subject.getAsync((result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                if (!result.value || result.value.trim() === '') {
                  item.subject.setAsync(template.subject, (setResult) => {
                    if (setResult.status === Office.AsyncResultStatus.Failed) {
                      console.error('Failed to set subject:', setResult.error.message);
                    }
                  });
                }
              }
            });
          }
          
          // Insert body at cursor position (not replace)
          if (template.body && item.body) {
            item.body.setSelectedDataAsync(
              template.body,
              { coercionType: Office.CoercionType.Html },
              (result) => {
                if (result.status === Office.AsyncResultStatus.Failed) {
                  console.error('Failed to insert template:', result.error.message);
                } else {
                  console.log('✅ Template inserted successfully at cursor position');
                }
              }
            );
          }
        }
      } catch (err) {
        console.error('Error applying template:', err);
      }
    }
  };

  return (
    <div className="border-b border-gray-300">
      <button
        className={`flex items-center justify-between w-full ${currentTextSize.cardPadding} hover:bg-gray-50 transition-colors`}
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <DocumentRegular className="h-4 w-4 text-gray-600" />
          <h3 className={`${currentTextSize.headerText} font-semibold text-gray-900`}>
            {t('compose.templates')}
          </h3>
        </div>
        <ChevronDownRegular
          className={`h-4 w-4 text-gray-500 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className={`px-4 pb-4 ${currentTextSize.spacing}`}>
          {/* Search Input */}
          <div className="relative mb-3">
            <SearchRegular className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('compose.searchTemplates')}
              className={`w-full pl-9 pr-3 py-2 border border-gray-300 rounded ${currentTextSize.bodyText} focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Templates List */}
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className={`${currentTextSize.bodyText} text-gray-500`}>
                {t('compose.loadingTemplates')}
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-4">
              <p className={`${currentTextSize.bodyText} text-red-600`}>
                {error}
              </p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-4">
              <p className={`${currentTextSize.bodyText} text-gray-500`}>
                {searchQuery ? t('incidentInfo.noProcessesFound') : t('compose.noTemplates')}
              </p>
            </div>
          ) : (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {filteredTemplates.map((template) => {
                const isSelected = selectedTemplateId === template.id;
                return (
                  <button
                    key={template.id}
                    onClick={() => handleTemplateSelect(template.id)}
                    className={`w-full text-left px-3 py-2 rounded transition-colors flex items-center justify-between ${
                      isSelected
                        ? 'bg-blue-50 border border-blue-200'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <span className={`${currentTextSize.bodyText} text-gray-900 truncate`}>
                      {template.name}
                    </span>
                    {isSelected && (
                      <CheckmarkRegular className="h-4 w-4 text-blue-600 ml-2 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

